/**
 * ProductDetailsFields
 *
 * Renders the three collapsible form sections (General / Spec / Storage) for
 * a product.  Used in two places inside ProductEdit:
 *   1. The "Details" tab in edit mode.
 *   2. The top card in create mode.
 *
 * Props pattern: individual setters (one `setX` per field).
 * Rationale: the parent already holds each field in a discrete useState call,
 * so passing those setters directly is the zero-abstraction wiring — no reducer
 * or onChange dispatcher needed, and the prop names map 1-to-1 with state names.
 */

import {
  AllergenChipPicker,
  CollapsibleSection,
  CountrySelect,
  DietaryChipPicker,
  Input,
  KeyValueEditor,
  Select,
  Textarea,
} from "@wmart/ui";
import type { AllergenCode, DietaryTagCode, StorageType } from "@wmart/shared-types";
import { useVatCategories } from "../lib/vat-categories.js";
import { useBrands } from "../lib/brands.js";
import { useSuppliers } from "../lib/suppliers.js";

// ---------------------------------------------------------------------------
// Constants (duplicated from ProductEdit to keep this file self-contained)
// ---------------------------------------------------------------------------

const STORAGE_TYPES: Array<{ value: StorageType | ""; label: string }> = [
  { value: "", label: "—" },
  { value: "ambient", label: "Ambient" },
  { value: "chilled", label: "Chilled" },
  { value: "frozen", label: "Frozen" },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ProductDetailsFieldsProps {
  // General
  slug: string;
  setSlug: (v: string) => void;
  title: string;
  setTitle: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  vatCategory: string;
  setVatCategory: (v: string) => void;
  status: "draft" | "active" | "archived";
  setStatus: (v: "draft" | "active" | "archived") => void;
  // Spec
  brandId: string | null;
  setBrandId: (v: string | null) => void;
  supplierId: string | null;
  setSupplierId: (v: string | null) => void;
  originCountry: string | null;
  setOriginCountry: (v: string | null) => void;
  allergens: AllergenCode[];
  setAllergens: (v: AllergenCode[]) => void;
  dietaryTags: DietaryTagCode[];
  setDietaryTags: (v: DietaryTagCode[]) => void;
  ingredients: string;
  setIngredients: (v: string) => void;
  specExtra: Record<string, string>;
  setSpecExtra: (v: Record<string, string>) => void;
  // Storage
  storageType: StorageType | null;
  setStorageType: (v: StorageType | null) => void;
  storageTempMinC: string;
  setStorageTempMinC: (v: string) => void;
  storageTempMaxC: string;
  setStorageTempMaxC: (v: string) => void;
  shelfLifeDaysUnopened: string;
  setShelfLifeDaysUnopened: (v: string) => void;
  shelfLifeDaysOpened: string;
  setShelfLifeDaysOpened: (v: string) => void;
  storageNotes: string;
  setStorageNotes: (v: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProductDetailsFields({
  slug, setSlug,
  title, setTitle,
  description, setDescription,
  vatCategory, setVatCategory,
  status, setStatus,
  brandId, setBrandId,
  supplierId, setSupplierId,
  originCountry, setOriginCountry,
  allergens, setAllergens,
  dietaryTags, setDietaryTags,
  ingredients, setIngredients,
  specExtra, setSpecExtra,
  storageType, setStorageType,
  storageTempMinC, setStorageTempMinC,
  storageTempMaxC, setStorageTempMaxC,
  shelfLifeDaysUnopened, setShelfLifeDaysUnopened,
  shelfLifeDaysOpened, setShelfLifeDaysOpened,
  storageNotes, setStorageNotes,
}: ProductDetailsFieldsProps) {
  const { data: vatCategories } = useVatCategories();
  const { data: brands } = useBrands();
  const { data: suppliers } = useSuppliers();

  return (
    <div className="flex flex-col gap-3">
      <CollapsibleSection title="General" defaultOpen>
        <div className="flex flex-col gap-4">
          <Input
            label="Slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            required
            className="font-mono"
          />
          <Input
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
          <Textarea
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
          <div className="flex gap-3">
            <div className="flex-1">
              <Select
                label="VAT category"
                value={vatCategory}
                onChange={(e) => setVatCategory(e.target.value)}
              >
                {vatCategories.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex-1">
              <Select
                label="Status"
                value={status}
                onChange={(e) =>
                  setStatus(e.target.value as "draft" | "active" | "archived")
                }
              >
                <option value="draft">draft</option>
                <option value="active">active</option>
                <option value="archived">archived</option>
              </Select>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Spec">
        <div className="flex flex-col gap-4">
          <div className="flex gap-3">
            <div className="flex-1">
              <Select
                label="Brand"
                value={brandId ?? ""}
                onChange={(e) => setBrandId(e.target.value || null)}
              >
                <option value="">— none —</option>
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.title}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-body-sm font-medium text-ink-700">
                Origin country
              </label>
              <CountrySelect value={originCountry} onChange={setOriginCountry} />
            </div>
          </div>
          <div className="flex-1">
            <Select
              label="Supplier"
              value={supplierId ?? ""}
              onChange={(e) => setSupplierId(e.target.value || null)}
            >
              <option value="">— WMart own stock (no supplier) —</option>
              {suppliers.map((s) => (
                <option
                  key={s.id}
                  value={s.id}
                  disabled={!s.active && s.id !== supplierId}
                >
                  {s.name}
                  {!s.active ? " (inactive)" : ""}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <label className="mb-2 block text-body-sm font-medium text-ink-700">
              Allergens
            </label>
            <AllergenChipPicker value={allergens} onChange={setAllergens} />
          </div>

          <div>
            <label className="mb-2 block text-body-sm font-medium text-ink-700">
              Dietary tags
            </label>
            <DietaryChipPicker value={dietaryTags} onChange={setDietaryTags} />
          </div>

          <Textarea
            label="Ingredients"
            value={ingredients}
            onChange={(e) => setIngredients(e.target.value)}
            rows={3}
          />

          <div>
            <label className="mb-2 block text-body-sm font-medium text-ink-700">
              Extra spec fields
            </label>
            <KeyValueEditor value={specExtra} onChange={setSpecExtra} />
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Storage">
        <div className="flex flex-col gap-4">
          <div className="flex gap-3">
            <div className="flex-1">
              <Select
                label="Storage type"
                value={storageType ?? ""}
                onChange={(e) =>
                  setStorageType(
                    e.target.value === "" ? null : (e.target.value as StorageType),
                  )
                }
              >
                {STORAGE_TYPES.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex-1">
              <Input
                label="Temp min (°C)"
                type="number"
                value={storageTempMinC}
                onChange={(e) => setStorageTempMinC(e.target.value)}
              />
            </div>
            <div className="flex-1">
              <Input
                label="Temp max (°C)"
                type="number"
                value={storageTempMaxC}
                onChange={(e) => setStorageTempMaxC(e.target.value)}
              />
            </div>
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                label="Shelf life unopened (days)"
                type="number"
                value={shelfLifeDaysUnopened}
                onChange={(e) => setShelfLifeDaysUnopened(e.target.value)}
              />
            </div>
            <div className="flex-1">
              <Input
                label="Shelf life opened (days)"
                type="number"
                value={shelfLifeDaysOpened}
                onChange={(e) => setShelfLifeDaysOpened(e.target.value)}
              />
            </div>
          </div>

          <Textarea
            label="Storage notes"
            value={storageNotes}
            onChange={(e) => setStorageNotes(e.target.value)}
            rows={2}
          />
        </div>
      </CollapsibleSection>
    </div>
  );
}
